import {createBrowserRouter, RouterProvider, ScrollRestoration, Outlet, useParams, useNavigate} from 'react-router'

import { Homepage } from './app/routes/homepage';
import { TopNav } from './app/components/topNav'
import { Expenses } from './app/routes/expenses';
import { NewExpense } from './app/routes/expenses/newExpense';
import { EditExpense } from './app/routes/expenses/editExpense';
import EditProfile from './app/routes/editProfile';
import IncidentsShow from './app/routes/incidents/show';
import ChangePassword from './app/routes/changePassword';
import { NotFound } from './app/screens/notFound';
import EditDependents from './app/routes/editDependents';
import { Help } from './app/routes/help';
import IncidentsSubmit from './app/routes/incidents/submit';
import SubmitHome from './app/routes/incidents/submit/home';
import SubmitShow from './app/routes/incidents/submit/show';
import { Todos } from './app/routes/todos';
import { TodoExplainerPage } from './app/components/todos/todoExplainerPage';
import { RedirectPlaceholder } from './app/components/redirectPlaceholder';
import SubmitPersonalResponsibility from './app/routes/incidents/submit/submitPersonalResponsibility';
import { DeleteAccount } from './app/routes/deleteAccount';
import EditSettings from './app/routes/editSettings';
import ConnectedAccounts from './app/routes/connectedAccounts';
import Subscription from './app/routes/subscription';
import TeamSignup from './app/routes/teamSignup';
import { RouteErrorFallback } from './app/components/error-boundary';
import { useEffect, useMemo, useState } from 'react';
import { IncidentsIndex } from './app/routes/incidents';
import { IncidentCreate } from './app/routes/incidents/incidentCreate';
import IncidentEdit from './app/routes/incidents/incidentEdit';
import AdvanceRequestCreate from './app/routes/incidents/advanceRequest/advanceRequestCreate';
import { AdvanceShow } from './app/routes/incidents/advance/show';
import AlertBanner from './app/components/alertBanner';
import { Notifications } from './app/routes/notifications';
import { Test } from './app/routes/test';
import { useAppSelector } from './app/hooks/reduxToolkit';
import { useNavigationListener } from './app/hooks/useNavigationListener';
import { NewProvider } from './app/routes/providers/newProvider';
import { ProviderShow } from './app/routes/providers/show';
import { ProviderConversations } from './app/routes/providers/conversations';
import { EditProvider } from './app/routes/providers/editProvider';
import { ExpenseRedirect } from './app/routes/expenses/expenseRedirect';
import { ProvidersIndex } from './app/routes/providers';
import { ReceiptLibrary } from './app/routes/receiptLibrary';
import { AttachToExpense } from './app/routes/receipts/attachToExpense';
import { LinkDraftToExpense } from './app/routes/expenses/linkDraftToExpense';
import { ConversationShow } from './app/routes/conversations/show';
import { AICallScreen } from './app/routes/providers/aiCall';
import { AliceIndex } from './app/routes/alice/index';
import { AliceConversationShow } from './app/routes/alice/show';
import { isEmbeddedApp } from './lib/nativeBridge';
import { AdminMfaRedirect } from './app/screens/adminMfaRequired';

// Exported for todoRoutes.webRouter.test.tsx, which asserts every published todo-route
// pattern still resolves here. Importing it runs no side effects: the router itself is
// built lazily inside App().
export const routeConfig = [
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        index: true,
        element: <Homepage />,
      },
      {
        path: 'changePassword',
        element: <ChangePassword />,
      },
      {
        path: 'profile',
        element: <EditProfile />,
      },
      {
        path: 'settings',
        element: <EditSettings />,
      },
      {
        // Laravel sends a signed-in backend account here when /admin refuses the
        // session. The sign-in router is unmounted once a session exists, so this
        // path has to live on the signed-in router or it falls through to NotFound.
        path: 'signIn/email',
        element: <AdminMfaRedirect />,
      },
      {
        path: 'connectedAccounts',
        element: <ConnectedAccounts />,
      },
      {
        path: 'subscription',
        element: <Subscription />,
      },
      {
        path: 'team-signup',
        element: <TeamSignup />,
      },
      {
        path: 'dependents',
        element: <EditDependents />,
      },
      {
        path: 'expenses',
        element: <Expenses />,
      },
      {
        path: 'expenses/new',
        element: <NewExpense />,
      },
      {
        path: 'expenses/:expenseId/edit',
        element: <EditExpense />,
      },
      {
        path: 'expenses/:draftId/link-to-expense',
        element: <LinkDraftToExpense />,
      },
      {
        path: 'incidents',
        element: <IncidentsIndex />,
      },
      {
        path: 'incidents/new',
        element: <IncidentCreate />,
      },
      {
        path: 'incidents/:id',
        element: <IncidentsShow />,
      },
      {
        path: 'incidents/:incidentId/edit',
        element: <IncidentEdit />,
      },
      {
        path: 'incidents/:incidentId/submit',
        element: <IncidentsSubmit />,
        children: [
          { index: true, element: <SubmitHome /> },
          { path: ':id', element: <SubmitShow /> },
          { path: ':submissionId/personal-responsibility', element: <SubmitPersonalResponsibility /> },
        ]
      },
      {
        path: 'incidents/:incidentId/advance-request/new',
        element: <AdvanceRequestCreate />,
      },
      {
        path: 'incidents/:incidentId/advances/:advanceId', 
        element: <AdvanceShow />,
      },
      {
        path: 'expenses/:id',
        element: <ExpenseRedirect />
      },
      {
        path: 'advance-requests/:advanceRequestId',
        element: <AdvanceRequestRedirect />
      },
      {
        path: 'advances/:advanceId',
        element: <AdvanceRedirect />
      },
      {
        path: 'providers/new',
        element: <NewProvider />,
      },
      {
        path: 'expenses/:expenseId/providers/new',
        element: <NewProvider />,
      },
      {
        path: 'providers/:id',
        element: <ProviderShow />,
      },
      {
        path: 'providers/:id/conversations',
        element: <ProviderConversations />,
      },
      {
        path: 'providers/:id/edit',
        element: <EditProvider />,
      },
      {
        path: 'providers/:providerId/call',
        element: <AICallScreen />,
      },
      {
        path: 'conversations/:id',
        element: <ConversationShow />,
      },
      {
        path: 'todos',
        element: <Todos />,
      },
      {
        path: 'todos/:id',
        element: <RedirectToTodo />,
      },
      {
        path: 'todos/:recordType/:recordId/:todoType/instructions',
        element: <TodoExplainerPage />,
      },
      {
        path: 'notifications',
        element: <Notifications />,
      },
      {
        path: 'help',
        element: <Help />,
      },
      {
        path: 'deleteAccount',
        element: <DeleteAccount />,
      },
      {
        path: 'test',
        element: <Test />,
      },
      {
        path: 'providers',
        element: <ProvidersIndex />,
      },
      {
        path: 'receipts',
        element: <ReceiptLibrary />,
      },
      {
        path: 'receipts/:blobKey/attach-to-expense',
        element: <AttachToExpense />,
      },
      {
        path: 'alice',
        element: <AliceIndex />,
      },
      {
        path: 'alice/:id',
        element: <AliceConversationShow />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  }
]

function Layout() {
  // Emit navigation-change events to the native Expo host (no-op in a browser).
  useNavigationListener()

  const isInsidePWA = useMemo(() => {
    const isInsidePWA = window.matchMedia('(display-mode: standalone)').matches
    return isInsidePWA
  }, [])

  // A page pushed under the native app bar. The full-app webview and a normal
  // browser visit leave this false, so their header and tabs stay.
  const embedded = isEmbeddedApp()
  
  return (
    <div className={`App container${isInsidePWA ? ' is-inside-pwa' : ''}${embedded ? ' is-embedded' : ''}`}>
      {embedded ? null : <TopNav />}
      <Outlet />
      <AlertBanner />
      <div className='mt-4' />
      <ScrollRestoration />
    </div>
  );
}

function App() {
  // Create the router lazily on first render, not at module load time.
  // This ensures createBrowserHistory captures window.location AFTER the startup
  // flow has cleaned the URL to '/', preventing the router from initializing at
  // a stale path like '/signIn/email' and rendering NotFound on first load.
  const [router] = useState(() => createBrowserRouter(routeConfig))
  return (
    <RouterProvider router={router} />
  );
}

export default App;

/**
 * Advance Requests are nested under Incidents, so we need to redirect to the Incident page.
 */
function AdvanceRequestRedirect() {
  const { advanceRequestId } = useParams()
  const navigate = useNavigate()
  
  const advanceRequest = useAppSelector((s) => s.advances.advanceRequests.find((ar) => ar.id === advanceRequestId))
  
  useEffect(() => {
    if (advanceRequest) {
      navigate(`/incidents/${advanceRequest.incident_id}`)
    }
  }, [advanceRequest])
  
  const [timedOut, setTimedOut] = useState(false)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setTimedOut(true)
    }, 1000)
    
    return () => clearTimeout(timeout)
  }, [advanceRequest])
  
  if (timedOut) {
    return <NotFound />
  }
  
  return <RedirectPlaceholder />
}

/**
 * Advances are nested under Incidents, so we need to redirect.
 */
function AdvanceRedirect() {
  const { advanceId } = useParams()
  const navigate = useNavigate()
  
  const advance = useAppSelector((s) => s.advances.advances.find((a) => a.id === advanceId))
  
  useEffect(() => {
    if (advance) {
      navigate(`/incidents/${advance.incident_id}/advances/${advance.id}`)
    }
  }, [advance])
  
  const [timedOut, setTimedOut] = useState(false)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setTimedOut(true)
    }, 1000)
    
    return () => clearTimeout(timeout)
  }, [advance])
  
  if (timedOut) {
    return <NotFound />
  }
  
  return <RedirectPlaceholder />
}

function RedirectToTodo() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  useEffect(() => {
    navigate(`/todos`, { replace: true, state: { highlightTodoId: id } })
  }, [])
  
  return <RedirectPlaceholder />
}
